import { useEffect, useState, useCallback } from "react";
import { ActionsButton } from "@/components/ActionsButton";
import { useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Plus, Search, FileText, Pencil, Trash2, Download, Printer, Copy, Send, FileCheck2, Paperclip, Upload, Layers, Mail, Truck,
} from "lucide-react";
import api, { cachedGet } from "@/lib/api";
import { euro, fmtDate, STATUS, formatApiErrorDetail } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import QuoteBuilder from "@/components/QuoteBuilder";
import QuotePDF from "@/components/QuotePDF";
import SendDialog from "@/components/SendDialog";
import AttachmentsDialog from "@/components/AttachmentsDialog";
import ImportQuoteDialog from "@/components/ImportQuoteDialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import { useCompanyGate } from "@/components/company/useCompanyGate";
import { downloadElementPdf } from "@/lib/pdf";

export default function Preventivi() {
  const [quotes, setQuotes] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [prices, setPrices] = useState([]);
  const [preziario, setPreziario] = useState([]);
  const [settings, setSettings] = useState(null);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("tutti");
  const [toDelete, setToDelete] = useState(null);
  const [sendDoc, setSendDoc] = useState(null);
  const [attachQuote, setAttachQuote] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [extractedInitial, setExtractedInitial] = useState(null);
  const [selected, setSelected] = useState([]);
  const [params, setParams] = useSearchParams();
  const { ok: profileOk, ready: profileReady, ensure, gate } = useCompanyGate();
  const navigate = useNavigate();
  const { user } = useAuth();

  const load = useCallback(() => {
    Promise.all([
      cachedGet("/quotes"), cachedGet("/contacts"), cachedGet("/prices"), cachedGet("/settings"), cachedGet("/preziario"),
    ]).then(([qs, cs, ps, st, pz]) => {
      setQuotes(qs.data); setContacts(cs.data); setPrices(ps.data); setSettings(st.data); setPreziario(pz.data.items || []);
    });
  }, []);

  useEffect(() => { load(); }, [load]);

  // Lo stato dei dialoghi è riflesso nell'URL: al refresh la vista aperta viene ripristinata.
  const viewId = params.get("view");
  const editId = params.get("edit");
  const dupId = params.get("duplicate");
  const isNew = params.get("new") === "1";

  const pdfQuote = viewId ? quotes.find((x) => x.id === viewId) || null : null;
  const inviaId = params.get("invia");
  useEffect(() => {
    if (!inviaId || !quotes.length) return;
    const q = quotes.find((x) => x.id === inviaId);
    if (q) setSendDoc(q);
  }, [inviaId, quotes]);
  const closeSend = () => {
    setSendDoc(null);
    if (inviaId) { const p = new URLSearchParams(params); p.delete("invia"); p.delete("canale"); setParams(p); }
  };
  const builderMode = editId ? "edit" : "new";
  const builderData = editId
    ? quotes.find((x) => x.id === editId) || null
    : dupId
    ? quotes.find((x) => x.id === dupId) || null
    : isNew
    ? extractedInitial
    : null;
  const builderOpen = (profileOk && (isNew || (!!dupId && !!builderData))) || (!!editId && !!builderData);

  const setDialogParam = (key, value) => {
    const p = new URLSearchParams(params);
    ["view", "edit", "duplicate", "new"].forEach((k) => p.delete(k));
    if (value != null) p.set(key, String(value));
    setParams(p);
  };
  const closeDialogs = () => {
    const p = new URLSearchParams(params);
    ["view", "edit", "duplicate", "new"].forEach((k) => p.delete(k));
    setParams(p);
    setExtractedInitial(null);
  };

  const save = async (payload) => {
    try {
      if (builderMode === "edit") await api.put(`/quotes/${builderData.id}`, payload);
      else await api.post("/quotes", payload);
      toast.success(builderMode === "edit" ? "Preventivo aggiornato" : "Preventivo creato");
      closeDialogs(); load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const openNew = () => { setExtractedInitial(null); setDialogParam("new", "1"); };
  // Primo preventivo di una ditta nuova: prima chiediamo i dati aziendali (visura o a mano).
  useEffect(() => {
    if ((isNew || dupId) && profileReady && !profileOk) ensure(null, closeDialogs);
  }, [isNew, dupId, profileReady, profileOk]); // eslint-disable-line react-hooks/exhaustive-deps
  const openEdit = (x) => setDialogParam("edit", x.id);
  const openDuplicate = (x) => setDialogParam("duplicate", x.id);
  const openPreview = (x) => setDialogParam("view", x.id);
  const onExtracted = (data) => {
    setExtractedInitial({ cliente_nome: data.cliente_nome || "", oggetto: data.oggetto || "", voci: data.voci || [], contact_id: "" });
    setImportOpen(false);
    setDialogParam("new", "1");
  };

  const convert = (x) => ensure(async () => {
    try {
      const r = await api.post(`/quotes/${x.id}/convert`);
      toast.success(`Fattura N. ${r.data.numero} creata`);
      navigate("/fatture");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  });

  const changeStatus = async (quote, stato) => {
    const r = await api.patch(`/quotes/${quote.id}/status?stato=${stato}`);
    if (r.data.invoice_created) {
      toast.success(`Fattura N. ${r.data.invoice_numero} creata in automatico`, {
        description: "Puoi modificare rate e pagamenti nella sezione Fatture.",
        action: { label: "Apri", onClick: () => navigate("/fatture") },
      });
    } else toast.success(STATUS[stato].label);
    load();
  };

  const sendReminder = async (x) => {
    try {
      const r = await api.post(`/quotes/${x.id}/send-reminder`);
      toast.success(`Promemoria inviato a ${r.data.to}`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const doDelete = async () => {
    await api.delete(`/quotes/${toDelete.id}`);
    toast.success("Preventivo eliminato");
    setToDelete(null); load();
  };

  const toggleSelect = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const mergeSelected = () => {
    const chosen = quotes.filter((x) => selected.includes(x.id));
    if (chosen.length < 2) return;
    const voci = chosen.flatMap((x) => x.voci || []);
    const sameContact = chosen.every((x) => x.contact_id && x.contact_id === chosen[0].contact_id);
    const sameCliente = chosen.every((x) => x.cliente_nome === chosen[0].cliente_nome);
    const oggetti = [...new Set(chosen.map((x) => x.oggetto).filter(Boolean))];
    setExtractedInitial({
      cliente_nome: sameCliente ? chosen[0].cliente_nome : "",
      contact_id: sameContact ? chosen[0].contact_id : "",
      oggetto: oggetti.join(" + ") || `Unione preventivi ${chosen.map((x) => "N." + x.numero).join(", ")}`,
      voci,
    });
    setSelected([]);
    setDialogParam("new", "1");
    toast.success(`${chosen.length} preventivi uniti in una nuova bozza`);
  };

  const filtered = quotes.filter((x) => {
    const matchQ = !q || x.cliente_nome?.toLowerCase().includes(q.toLowerCase()) || x.numero?.includes(q) || x.oggetto?.toLowerCase().includes(q.toLowerCase());
    const matchF = filter === "tutti" || x.stato === filter;
    return matchQ && matchF;
  });

  const contactOf = (quote) => contacts.find((c) => c.id === quote?.contact_id);

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="relative w-full sm:w-80">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input placeholder="Cerca cliente, N. o oggetto…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" data-testid="quotes-search-input" />
        </div>
        <div className="flex gap-2">
          {selected.length >= 2 && (
            <Button variant="secondary" onClick={mergeSelected} data-testid="merge-quotes-button">
              <Layers className="w-4 h-4 mr-1.5" /> Unisci {selected.length}
            </Button>
          )}
          <Button variant="outline" onClick={() => setImportOpen(true)} data-testid="import-quote-button">
            <Upload className="w-4 h-4 mr-1.5" /> Importa da file
          </Button>
          <Button onClick={openNew} data-testid="new-quote-trigger-button">
            <Plus className="w-4 h-4 mr-1.5" /> Nuovo Preventivo
          </Button>
        </div>
      </div>

      <Tabs value={filter} onValueChange={setFilter}>
        <TabsList data-testid="quotes-status-tabs">
          <TabsTrigger value="tutti">Tutti</TabsTrigger>
          <TabsTrigger value="bozza">Bozza</TabsTrigger>
          <TabsTrigger value="inviato">Inviato</TabsTrigger>
          <TabsTrigger value="accettato">Accettato</TabsTrigger>
          <TabsTrigger value="rifiutato">Rifiutato</TabsTrigger>
        </TabsList>
      </Tabs>

      <div className="rounded-xl border border-border bg-card overflow-hidden">
        <Table data-testid="quotes-table">
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead className="w-8"></TableHead>
              <TableHead>N.</TableHead>
              <TableHead>Cliente</TableHead>
              <TableHead className="hidden md:table-cell">Emissione</TableHead>
              <TableHead className="hidden md:table-cell">Scadenza</TableHead>
              <TableHead className="text-right">Totale</TableHead>
              <TableHead>Stato</TableHead>
              <TableHead className="w-10"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((x) => (
              <TableRow key={x.id} className="cursor-pointer" onClick={() => openPreview(x)} data-testid={`quote-row-${x.numero}`}>
                <TableCell className="w-8" onClick={(e) => e.stopPropagation()}>
                  <input type="checkbox" checked={selected.includes(x.id)} onChange={() => toggleSelect(x.id)} className="accent-primary w-4 h-4 align-middle" data-testid={`quote-select-${x.numero}`} />
                </TableCell>
                <TableCell className="font-mono text-xs font-medium">{x.numero}</TableCell>
                <TableCell>
                  <div className="font-medium">{x.cliente_nome}</div>
                  {x.oggetto && <div className="text-xs text-muted-foreground truncate max-w-[220px]">{x.oggetto}</div>}
                </TableCell>
                <TableCell className="hidden md:table-cell text-sm text-muted-foreground">{fmtDate(x.data_emissione)}</TableCell>
                <TableCell className="hidden md:table-cell text-sm text-muted-foreground">{fmtDate(x.scadenza)}</TableCell>
                <TableCell className="text-right font-semibold">{euro(x.totale)}</TableCell>
                <TableCell onClick={(e) => e.stopPropagation()}>
                  <Select value={x.stato} onValueChange={(s) => changeStatus(x, s)}>
                    <SelectTrigger className={`h-8 w-[130px] text-sm border ${STATUS[x.stato]?.cls}`} data-testid={`quote-status-inline-${x.numero}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="bozza">Bozza</SelectItem>
                      <SelectItem value="inviato">Inviato</SelectItem>
                      <SelectItem value="accettato">Accettato</SelectItem>
                      <SelectItem value="rifiutato">Rifiutato</SelectItem>
                    </SelectContent>
                  </Select>
                </TableCell>
                <TableCell onClick={(e) => e.stopPropagation()}>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <ActionsButton data-testid={`quote-actions-${x.numero}`} />
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => openPreview(x)}><FileText className="w-4 h-4 mr-2" /> Anteprima / PDF</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => openEdit(x)}><Pencil className="w-4 h-4 mr-2" /> Modifica</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => openDuplicate(x)} data-testid={`quote-duplicate-${x.numero}`}><Copy className="w-4 h-4 mr-2" /> Duplica</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setAttachQuote(x)} data-testid={`quote-attach-${x.numero}`}><Paperclip className="w-4 h-4 mr-2" /> Allegati / Firmato</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setSendDoc(x)} data-testid={`quote-send-${x.numero}`}><Send className="w-4 h-4 mr-2" /> Invia (WhatsApp/Email)</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate(`/ddt?from_quote=${x.id}`)} data-testid={`quote-ddt-${x.numero}`}><Truck className="w-4 h-4 mr-2" /> Crea DDT (bolla)</DropdownMenuItem>
                      {user?.role === "admin" && settings?.email_reminders_enabled && <DropdownMenuItem onClick={() => sendReminder(x)} data-testid={`quote-remind-${x.numero}`}><Mail className="w-4 h-4 mr-2" /> Invia promemoria email</DropdownMenuItem>}
                      {user?.role === "admin" && <DropdownMenuItem onClick={() => convert(x)} data-testid={`quote-convert-${x.numero}`}><FileCheck2 className="w-4 h-4 mr-2" /> Converti in fattura</DropdownMenuItem>}
                      <DropdownMenuSeparator />
                      <DropdownMenuItem className="text-destructive" onClick={() => setToDelete(x)}><Trash2 className="w-4 h-4 mr-2" /> Elimina</DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
            {filtered.length === 0 && (
              <TableRow><TableCell colSpan={8} className="text-center py-12 text-muted-foreground">Nessun preventivo trovato.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <QuoteBuilder
        open={builderOpen}
        onClose={closeDialogs}
        onSave={save}
        contacts={contacts}
        prices={prices}
        preziario={preziario}
        initial={builderData}
        mode={builderMode}
      />

      {/* PDF preview dialog */}
      <Dialog open={!!pdfQuote} onOpenChange={(o) => !o && closeDialogs()}>
        <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto p-0">
          <DialogTitle className="sr-only">Anteprima preventivo N. {pdfQuote?.numero}</DialogTitle>
          <DialogDescription className="sr-only">Anteprima e download del preventivo in PDF.</DialogDescription>
          <div className="sticky top-0 z-10 flex items-center justify-between px-5 py-3 border-b border-border bg-card no-print">
            <div className="font-heading font-semibold">Anteprima preventivo N. {pdfQuote?.numero}</div>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" onClick={() => window.print()}><Printer className="w-4 h-4 mr-1.5" /> Stampa</Button>
              <Button size="sm" onClick={() => downloadElementPdf("print-area", `Preventivo-${pdfQuote?.numero}.pdf`)} data-testid="quote-pdf-export-button"><Download className="w-4 h-4 mr-1.5" /> Scarica PDF</Button>
            </div>
          </div>
          <div className="p-4 bg-slate-100 dark:bg-slate-900">
            <QuotePDF quote={pdfQuote} settings={settings} contact={contactOf(pdfQuote)} />
          </div>
        </DialogContent>
      </Dialog>

      <SendDialog open={!!sendDoc} onClose={closeSend} doc={sendDoc} contact={contactOf(sendDoc)} settings={settings} docType="preventivo" initialChannel={params.get("canale") === "email" ? "email" : "whatsapp"} />
      {gate}
      <AttachmentsDialog open={!!attachQuote} onClose={() => setAttachQuote(null)} quote={attachQuote} onChanged={load} />
      <ImportQuoteDialog open={importOpen} onClose={() => setImportOpen(false)} onExtracted={onExtracted} />

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare il preventivo?</AlertDialogTitle>
            <AlertDialogDescription>Il preventivo N. {toDelete?.numero} sarà eliminato definitivamente.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="confirm-delete-quote">Elimina</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
